import { useQuery } from '@apollo/client';
import {
  CourseraTierSubscriptions_AcquisitionType as AcquisitionType,
  CourseraTierSubscriptions_PaymentCadence as PaymentCadence,
  CourseraTierSubscriptions_ProductType as ProductType,
  Subscriptions_PaymentProcessor as SubscriptionPaymentProcessor,
} from '__generated__/graphql-types';

import { useLocation } from 'js/lib/useRouter';

import GetSubscriptionsEligibleForCourseraPlusUpgrade from 'bundles/coursera-plus/api/GetSubscriptionsEligibleForCourseraPlusUpgradeQuery.graphql';
import type {
  GetSubscriptionsEligibleForCourseraPlusUpgradeQuery,
  GetSubscriptionsEligibleForCourseraPlusUpgradeQueryVariables,
  SubscriptionEligibleForUpgradeFragment,
} from 'bundles/coursera-plus/api/__generated__/GetSubscriptionsEligibleForCourseraPlusUpgradeQuery';
import courseraPlusExperiments from 'bundles/epic/clients/courseraPlus';
import QueryCourseraTierProductConfigurations from 'bundles/payments-common/api/QueryCourseraTierProductConfigurations.graphql';
import type {
  PaymentSchemeFragment,
  QueryCourseraTierProductConfigurationsQuery,
  QueryCourseraTierProductConfigurationsQueryVariables,
} from 'bundles/payments-common/api/__generated__/QueryCourseraTierProductConfigurations';

const PLUS_MONTHLY_UPGRADE_EMAIL_QUERY_PARAM = 'showS12nToPlusMonthlyUpgrade';
const PLUS_ANNUAL_UPGRADE_EMAIL_QUERY_PARAM = 'showS12nToPlusAnnualUpgrade';

export type SubscriptionEligibleForUpgrade = SubscriptionEligibleForUpgradeFragment;

export const useIsFromS12nToPlusUpgradeEmail = () => {
  const { query } = useLocation();
  const isRolloutEnabled = courseraPlusExperiments.get('s12nToCourseraPlusEmailFlowEnabled');

  const isFromS12nToPlusMonthlyUpgradeEmail =
    isRolloutEnabled && Boolean(query?.[PLUS_MONTHLY_UPGRADE_EMAIL_QUERY_PARAM]);
  const isFromS12nToPlusAnnualUpgradeEmail =
    isRolloutEnabled && Boolean(query?.[PLUS_ANNUAL_UPGRADE_EMAIL_QUERY_PARAM]);

  return {
    isFromS12nToPlusMonthlyUpgradeEmail,
    isFromS12nToPlusAnnualUpgradeEmail,
  };
};

export const useS12nToPlusUpgradeData = ({
  isCourseraPlusAnnualUpgrade,
  skip,
}: {
  isCourseraPlusAnnualUpgrade?: boolean;
  skip?: boolean;
}) => {
  const { data: eligibleSubscriptionData, loading: loadingEligibleSubscriptionData } = useQuery<
    GetSubscriptionsEligibleForCourseraPlusUpgradeQuery,
    GetSubscriptionsEligibleForCourseraPlusUpgradeQueryVariables
  >(GetSubscriptionsEligibleForCourseraPlusUpgrade, {
    variables: {
      toBillingCycle: isCourseraPlusAnnualUpgrade ? 'ANNUAL' : 'MONTHLY',
      toProductType: 'COURSERA_PLUS_SUBSCRIPTION',
    },
    context: { clientName: 'gatewayGql' },
    skip,
  });

  const subscriptions =
    eligibleSubscriptionData?.CourseraTierSubscriptionsQueries?.querySubscriptionsEligibleForUpgrade ?? [];
  const s12nEligibleSubscriptions = subscriptions.filter(
    ({ productId, paymentProcessor }) =>
      productId.productType === 'SPECIALIZATION' &&
      [
        SubscriptionPaymentProcessor.StripeBvConsumer,
        SubscriptionPaymentProcessor.StripeBvConsumerSandbox,
        SubscriptionPaymentProcessor.StripeConsumer,
        SubscriptionPaymentProcessor.StripeConsumerSandbox,
      ].includes(paymentProcessor)
  );
  // BE endpoint is agnostic to the product feature and can also return Lite subscriptions eligible for Plus upgrade
  const hasOtherEligibleSubscriptions = subscriptions.length - s12nEligibleSubscriptions.length > 0;
  // We only support upgrading exactly 1 eligible s12n subscription to C+ for the initial launch
  const isEligibleForS12nToPlusUpgrade = s12nEligibleSubscriptions?.length === 1 && !hasOtherEligibleSubscriptions;

  // TODO still ongoing discussion about how to fetch correct monthly price, or if price will be standardized
  // See https://coursera.slack.com/archives/C08DPP1UCGG/p1740590820836589
  const { data: courseraPlusData, loading: loadingCourseraPlusData } = useQuery<
    QueryCourseraTierProductConfigurationsQuery,
    QueryCourseraTierProductConfigurationsQueryVariables
  >(QueryCourseraTierProductConfigurations, {
    variables: {
      acquisitionType: AcquisitionType.Upgrade,
      paymentCadence: isCourseraPlusAnnualUpgrade
        ? PaymentCadence.AnnualSubscription
        : PaymentCadence.MonthlySubscription,
      tier: ProductType.CourseraPlusSubscription,
    },
    context: { clientName: 'gatewayGql' },
    skip: skip || !isEligibleForS12nToPlusUpgrade,
  });

  const courseraPlusPrice =
    courseraPlusData?.CourseraTierSubscriptionsQueries?.queryCourseraTierProductConfigurations?.find(
      ({ fulfillmentConfiguration }) =>
        (fulfillmentConfiguration?.paymentScheme as PaymentSchemeFragment)?.numberOfTrialDays === 31
    )?.price?.amount;

  return {
    isEligibleForS12nToPlusUpgrade,
    subscriptions: s12nEligibleSubscriptions,
    courseraPlusPrice,
    loading: loadingEligibleSubscriptionData || loadingCourseraPlusData,
  };
};
